import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';

interface WorkforceItem {
  team?: string;
  assignedTeam?: string;
  function?: string;
  quantity?: number;
  observations?: string;
  type?: 'direta' | 'terceirizada' | 'team' | 'individual';
  name?: string;
  // Team entry properties
  teamId?: string;
  teamName?: string;
  totalMembers?: number;
  members?: Array<{
    name: string;
    role: string;
    qty: number;
  }>;
  employeeType?: 'direta' | 'terceirizada';
}

interface WorkforceTableLayoutProps {
  workforce: WorkforceItem[];
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
}

export const WorkforceTableLayout: React.FC<WorkforceTableLayoutProps> = ({
  workforce,
  onEdit,
  onDelete
}) => {
  // Calcular totais considerando tanto entradas individuais quanto de equipe
  const workforceWithType = workforce.map(item => ({
    ...item,
    type: item.type || (item.team?.toLowerCase().includes('terceirizada') ? 'terceirizada' : 'direta')
  }));

  // Separar entradas por tipo (team vs individual) e por tipo de funcionário
  const teamEntries = workforceWithType.filter(item => item.type === 'team');
  const individualEntries = workforceWithType.filter(item => item.type !== 'team');

  // Calcular totais considerando ambos os tipos
  const diretaTeams = teamEntries.filter(item => item.employeeType === 'direta');
  const terceiraTeams = teamEntries.filter(item => item.employeeType === 'terceirizada');
  const diretaIndividuals = individualEntries.filter(item => item.type === 'direta');
  const terceiraIndividuals = individualEntries.filter(item => item.type === 'terceirizada');

  const totalDireta = diretaTeams.reduce((sum, team) => sum + (team.totalMembers || 0), 0) +
                      diretaIndividuals.reduce((sum, item) => sum + (item.quantity || 0), 0);
  
  const totalTerceirizada = terceiraTeams.reduce((sum, team) => sum + (team.totalMembers || 0), 0) +
                           terceiraIndividuals.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Agrupar por equipe para totais por equipe (apenas entradas individuais)
  const teamTotals = individualEntries.reduce((acc, item) => {
    const teamName = item.assignedTeam || item.team || 'Sem equipe';
    acc[teamName] = (acc[teamName] || 0) + (item.quantity || 0);
    return acc;
  }, {} as Record<string, number>);

  // Adicionar totais das equipes registradas como 'team'
  teamEntries.forEach(team => {
    const teamName = team.teamName || 'Equipe';
    teamTotals[teamName] = (teamTotals[teamName] || 0) + (team.totalMembers || 0);
  });

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Profissão</TableHead>
              <TableHead>Equipe</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Quantidade</TableHead>
              {onDelete && <TableHead className="text-right w-12">Ações</TableHead>}
            </TableRow>
          </TableHeader>
           <TableBody>
            {workforceWithType.flatMap((item, index) => {
              const isTeamEntry = item.type === 'team';
              
              if (isTeamEntry && item.members && item.members.length > 0) {
                // Expandir equipe em linhas separadas para cada membro
                return item.members.map((member, memberIndex) => {
                  const displayType = item.employeeType === 'direta' ? 'direta' : 'terceirizada';
                  
                  return (
                    <TableRow 
                      key={`${index}-member-${memberIndex}`}
                      className={onEdit ? "cursor-pointer hover:bg-muted/50" : ""}
                      onClick={() => onEdit?.(index)}
                    >
                      <TableCell className="font-medium">
                        {member.name || '—'}
                      </TableCell>
                      <TableCell>
                        {member.role}
                      </TableCell>
                      <TableCell>
                        {item.teamName}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={displayType === 'direta' ? 'default' : 'secondary'}
                          className={
                            displayType === 'direta' 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                          }
                        >
                          {displayType === 'direta' ? 'Próprio' : 'Terceirizado'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {member.qty}
                      </TableCell>
                      {onDelete && (
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(index);
                            }}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                });
              } else {
                // Entrada individual ou equipe sem membros
                const displayName = isTeamEntry ? item.teamName : (item.name || '—');
                const displayFunction = isTeamEntry ? 'Equipe' : (item.function || '—');
                const displayTeam = isTeamEntry 
                  ? item.teamName 
                  : (item.assignedTeam || item.team || 'Sem equipe');
                const displayQuantity = isTeamEntry ? (item.totalMembers || 0) : (item.quantity || 0);
                const displayType = isTeamEntry 
                  ? (item.employeeType === 'direta' ? 'direta' : 'terceirizada')
                  : item.type;

                return [
                  <TableRow 
                    key={index}
                    className={onEdit ? "cursor-pointer hover:bg-muted/50" : ""}
                    onClick={() => onEdit?.(index)}
                  >
                    <TableCell className="font-medium">
                      {displayName}
                    </TableCell>
                    <TableCell>
                      {displayFunction}
                    </TableCell>
                    <TableCell>
                      {displayTeam}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={displayType === 'direta' ? 'default' : 'secondary'}
                        className={
                          displayType === 'direta' 
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                        }
                      >
                        {displayType === 'direta' ? 'Próprio' : 'Terceirizado'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {displayQuantity}
                    </TableCell>
                    {onDelete && (
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(index);
                          }}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ];
              }
            })}
           </TableBody>
        </Table>
      </div>

      {/* Totais */}
      <div className="space-y-3 border-t pt-4">
        {/* Total por equipe */}
        <div>
          <h4 className="text-sm font-semibold mb-2">Total por Equipe:</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(teamTotals).map(([team, total]) => (
              <div key={team} className="flex justify-between items-center bg-muted/30 rounded px-3 py-2">
                <span className="text-sm">{team}</span>
                <span className="font-medium">{total}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Totais gerais */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="text-sm text-blue-700 mb-1">Total Próprios</div>
            <div className="text-2xl font-bold text-blue-600">{totalDireta}</div>
            <div className="text-xs text-blue-600">pessoas</div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="text-sm text-orange-700 mb-1">Total Terceirizados</div>
            <div className="text-2xl font-bold text-orange-600">{totalTerceirizada}</div>
            <div className="text-xs text-orange-600">pessoas</div>
          </div>
        </div>
      </div>
    </div>
  );
};